import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type LessonDetail } from '../../../lib/api.js'
import { useLocale } from '../../../i18n/index.js'
import { Icon } from '../../../classroom/icons.js'
import { ErrorLine, Skeleton } from '../../../classroom/ui.js'
import { stopSpeaking } from '../../../lib/speak.js'
import { PartBoundary } from '../PartBoundary.js'
import { playerCopy, type PlayerStrings } from './copy.js'
import { PlayerContext, type ExerciseMemory, type PlayerApi, type Primary, type Strip, type TeacherState } from './context.js'
import { ExerciseStep } from './ExerciseStep.js'
import { CoverStep, FinishStep, ListenStep, TextStep, WordsStep } from './StepViews.js'
import { buildSteps, HOMEWORK_KEY, outlineOf, sectionLabelPl, type Step } from './steps.js'
import { PolishKeys, usePolishKeys } from './PolishKeys.js'

/**
 * The lesson player (design/style-c LP-*, LP-Desk-*): one step at a time, a
 * progress bar for the whole lesson, the step outline on the left on a wide
 * screen, one primary action at the bottom. The step lives in the URL
 * (`?krok=N`), so reload and the browser's back button keep the place.
 *
 * Nothing is locked: the outline jumps anywhere, „Pomiń” skips an exercise,
 * and progress only ever shows where you are.
 */
export function LessonPlayer() {
  const { slug = '', lessonSlug = '' } = useParams()
  const locale = useLocale()
  const t = playerCopy(locale)
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['course', slug, 'lesson', lessonSlug],
    queryFn: () => api.course.lesson(slug, lessonSlug),
  })

  if (isPending || isError || !data) {
    return (
      <Frame t={t} exitTo={`/panel/kurs/${slug}`} title="" sub="" progress={0}>
        <div className="mx-auto w-full max-w-[660px] px-5 py-10 desk:px-12">
          {isPending ? (
            <Skeleton lines={5} />
          ) : (
            <div className="flex flex-col gap-4">
              <ErrorLine>{t.error.step}</ErrorLine>
              <button type="button" className="cl-btn cl-btn-primary self-start" onClick={() => refetch()}>
                {t.error.retry}
              </button>
            </div>
          )}
        </div>
      </Frame>
    )
  }
  return <Player lesson={data} slug={slug} t={t} />
}

/** What the teacher view (Studio) puts around the same player. */
export type TeacherFrame = {
  state: TeacherState
  /** The student switch and the key toggle, in the top bar. */
  tools: ReactNode
  /** Where ✕ and the last „Dalej” go. */
  exitTo: string
  /** Replaces the student's finish screen (which would mark the lesson done). */
  finish: ReactNode
}

export function Player({ lesson, slug, t, teacher }: { lesson: LessonDetail; slug: string; t: PlayerStrings; teacher?: TeacherFrame }) {
  const locale = useLocale()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()

  // The steps depend on the lesson's text, not on its attempts: rebuilding them
  // after every answer would remount the exercise being worked on.
  const textKey = `${lesson.id}:${lesson.sections.map((s) => s.id).join(',')}:${lesson.exercises.length}`
  const withHomework = Boolean(teacher)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const steps = useMemo(() => buildSteps(lesson, locale, { homework: withHomework }), [textKey, locale, withHomework])
  const outline = useMemo(() => outlineOf(lesson, steps), [lesson, steps])
  const fresh = useMemo(() => new Map(lesson.exercises.map((e) => [e.id, e])), [lesson.exercises])

  const raw = Number(params.get('krok') ?? '1')
  const index = Math.min(steps.length - 1, Math.max(0, Number.isFinite(raw) ? raw - 1 : 0))
  const step = steps[index]!

  /* ── what the step asks of the frame ─────────────────────────────── */
  const [primary, setPrimaryState] = useState<Primary | null>(null)
  const [strip, setStripState] = useState<Strip | null>(null)
  const [counter, setCounterState] = useState<string | null>(null)
  const back = useRef<(() => boolean) | null>(null)
  // The teacher view starts every exercise afresh when the key or the student changes.
  const mode = teacher ? `${teacher.state.keyShown ? 'key' : 'live'}:${teacher.state.student ?? ''}` : ''
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memory = useMemo(() => new Map<string, ExerciseMemory>(), [mode])
  const scroller = useRef<HTMLDivElement>(null)

  /* ── visited steps, for the outline's dots ───────────────────────── */
  const storeKey = `player.visited.${teacher ? 't.' : ''}${lesson.id}`
  const [visited, setVisited] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(storeKey) ?? '[]') as string[])
    } catch {
      return new Set()
    }
  })
  useEffect(() => {
    setVisited((v) => {
      if (v.has(step.id)) return v
      const next = new Set(v).add(step.id)
      try {
        localStorage.setItem(storeKey, JSON.stringify([...next]))
      } catch {
        /* private mode: the dots just reset next time */
      }
      return next
    })
  }, [step.id, storeKey])

  const goTo = useCallback(
    (i: number) => {
      stopSpeaking()
      const n = Math.min(steps.length - 1, Math.max(0, i))
      setParams((p) => {
        const q = new URLSearchParams(p)
        q.set('krok', String(n + 1))
        return q
      })
      scroller.current?.scrollTo({ top: 0 })
    },
    [steps.length, setParams],
  )
  const next = useCallback(() => goTo(index + 1), [goTo, index])
  const prev = () => {
    if (back.current?.()) return
    goTo(index - 1)
  }

  const api_: PlayerApi = useMemo(
    () => ({
      locale,
      t,
      next,
      goTo,
      setPrimary: setPrimaryState,
      setStrip: setStripState,
      setCounter: setCounterState,
      setBack: (fn) => {
        back.current = fn
      },
      memory,
      teacher: teacher?.state ?? null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, t, next, goTo, memory, teacher?.state.keyShown, teacher?.state.student],
  )
  const exit = teacher?.exitTo ?? `/panel/kurs/${slug}`

  const complete = useMutation({
    mutationFn: () => api.course.complete(lesson.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['course'] }),
  })
  const onComplete = useCallback(() => complete.mutate(), [complete])

  const keys = usePolishKeys(scroller)
  const part = step.sectionKey ? lesson.sections.find((s) => s.key === step.sectionKey) : null
  const hasHomework = lesson.exercises.some((e) => e.sectionKey === HOMEWORK_KEY)

  const exerciseOf = (s: Step) => (s.kind === 'exercise' ? fresh.get(s.exercise.id) ?? s.exercise : null)

  const body: ReactNode = (() => {
    switch (step.kind) {
      case 'cover':
        return <CoverStep lesson={lesson} />
      case 'words':
        return <WordsStep key={step.id} entries={step.entries} group={step.group} />
      case 'text': {
        // Pictures a part points at follow its first text step.
        const first = steps.find((s) => s.kind === 'text' && s.sectionKey === step.sectionKey)
        const figures = first?.id === step.id ? lesson.figures.filter((f) => f.sectionKey === step.sectionKey && f.exerciseRef === null) : []
        return <TextStep key={step.id} step={step} figures={figures} />
      }
      case 'listen':
        return <ListenStep key={step.id} step={step} audio={lesson.audio} />
      case 'exercise': {
        const e = exerciseOf(step)!
        return <ExerciseStep key={`${step.id}:${mode}`} exercise={e} figures={lesson.figures.filter((f) => f.exerciseRef === e.ref)} />
      }
      case 'finish':
        return teacher ? teacher.finish : <FinishStep lesson={lesson} slug={slug} steps={steps} onComplete={onComplete} />
    }
  })()

  return (
    <PlayerContext.Provider value={api_}>
      <Frame
        t={t}
        exitTo={exit}
        tools={teacher?.tools}
        scrollRef={scroller}
        title={`${t.lesson(lesson.number)} · ${lesson.title}`}
        sub={[part ? sectionLabelPl(part) : null, t.stepOf(index + 1, steps.length)].filter(Boolean).join(' · ')}
        progress={steps.length > 1 ? index / (steps.length - 1) : 0}
        counter={counter}
        outline={
          <Outline
            groups={outline}
            index={index}
            visited={visited}
            exerciseOf={exerciseOf}
            goTo={goTo}
            homework={hasHomework && !teacher ? `/panel/kurs/${slug}/${lesson.slug}/zadanie` : null}
            t={t}
          />
        }
        strip={strip}
        keys={keys.target ? <PolishKeys onKey={keys.insert} label={t.ex.letters} /> : null}
        bottom={
          <>
            <button
              type="button"
              onClick={prev}
              disabled={index === 0 && !back.current}
              aria-label={t.back}
              className="inline-flex h-[52px] w-[52px] shrink-0 cursor-pointer items-center justify-center rounded-full border border-class-sky-200 bg-class-white text-class-sky-700 disabled:opacity-40 desk:hidden"
            >
              <Icon name="back" />
            </button>
            <button
              type="button"
              onClick={prev}
              disabled={index === 0 && !back.current}
              className="cl-link-quiet hidden min-h-12 px-2 disabled:invisible desk:inline-flex desk:items-center"
            >
              ‹ {t.back}
            </button>
            <span className="hidden flex-1 desk:block" />
            {primary?.skip && (
              <button type="button" onClick={next} className="cl-link-quiet hidden min-h-12 px-3 desk:inline-flex desk:items-center">
                {t.skip} ›
              </button>
            )}
            <button
              type="button"
              onClick={() => (primary ? primary.onClick() : index < steps.length - 1 ? next() : navigate(exit))}
              disabled={primary?.disabled}
              className="cl-btn cl-btn-primary min-h-[52px] flex-1 desk:min-h-[50px] desk:flex-none desk:px-8"
            >
              {primary?.label ?? `${t.next} ›`}
            </button>
          </>
        }
      >
        <div
          className={[
            'mx-auto w-full px-5 pt-2 pb-8 desk:px-12 desk:pt-12 desk:pb-14',
            step.kind === 'words' ? 'max-w-[960px]' : 'max-w-[660px]',
          ].join(' ')}
        >
          <PartBoundary
            resetKey={step.id}
            fallback={
              <div className="flex flex-col gap-4">
                <ErrorLine>{t.error.step}</ErrorLine>
                <button type="button" onClick={next} className="cl-link self-start">
                  {t.error.skip} ›
                </button>
              </div>
            }
          >
            {body}
          </PartBoundary>
        </div>
      </Frame>
    </PlayerContext.Provider>
  )
}

/* ── the frame ─────────────────────────────────────────────────────── */

export function Frame({
  t,
  exitTo,
  tools,
  scrollRef,
  title,
  sub,
  progress,
  counter,
  outline,
  strip,
  keys,
  bottom,
  children,
}: {
  t: PlayerStrings
  exitTo: string
  tools?: ReactNode
  scrollRef?: RefObject<HTMLDivElement>
  title: string
  sub: string
  progress: number
  counter?: string | null
  outline?: ReactNode
  strip?: Strip | null
  keys?: ReactNode
  bottom?: ReactNode
  children: ReactNode
}) {
  const pct = `${Math.round(progress * 100)}%`
  return (
    <div className="cl flex h-dvh flex-col overflow-hidden bg-class-paper leading-[1.55]">
      {/* top bar */}
      <header className="flex shrink-0 items-center gap-3.5 px-5 py-4 desk:h-[72px] desk:gap-[18px] desk:border-b desk:border-class-field desk:bg-class-white desk:px-6 desk:py-0">
        <Link
          to={exitTo}
          aria-label={t.exit}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full !text-class-ink-soft desk:h-11 desk:w-11 desk:bg-class-field desk:!text-class-ink"
        >
          <Icon name="close" size={20} />
        </Link>
        <div className="hidden min-w-[190px] leading-tight desk:block">
          <p lang="pl" className="m-0 truncate font-class-display text-class-body">
            {title}
          </p>
          <p className="m-0 mt-0.5 text-class-label font-semibold text-class-ink-faint">{sub}</p>
        </div>
        <div
          role="progressbar"
          aria-label={t.progress}
          aria-valuenow={Math.round(progress * 100)}
          className="h-1.5 flex-1 overflow-hidden rounded-[3px] bg-class-field desk:ml-auto desk:max-w-[520px]"
        >
          <div className="h-full rounded-[3px] bg-class-gold transition-[width] duration-300" style={{ width: pct }} />
        </div>
        {counter && <span className="text-class-label font-semibold text-class-ink-faint desk:hidden">{counter}</span>}
        {tools && <div className="flex shrink-0 items-center gap-2 desk:gap-3">{tools}</div>}
      </header>

      <div className="flex min-h-0 flex-1">
        {outline && (
          <aside className="cl-scroll hidden w-[290px] shrink-0 flex-col gap-[22px] overflow-y-auto border-r border-class-field bg-[#f6faff] px-4 py-7 text-class-gloss desk:flex">
            {outline}
          </aside>
        )}
        <div ref={scrollRef} className="cl-scroll min-w-0 flex-1 overflow-y-auto" data-player-scroll>
          {children}
        </div>
      </div>

      {strip && (
        <div
          role="status"
          className={[
            'flex shrink-0 flex-wrap items-center gap-x-3.5 gap-y-1 px-5 py-3 desk:px-6',
            strip.tone === 'ok' ? 'bg-class-correct-100 text-class-correct' : strip.tone === 'bad' ? 'bg-class-wrong-100 text-class-wrong' : 'bg-class-field text-class-ink',
          ].join(' ')}
        >
          <span className="font-bold">{strip.text}</span>
          {strip.score && <span className="font-bold">{strip.score}</span>}
          {strip.action && (
            <button
              type="button"
              onClick={strip.action.onClick}
              className="ml-auto min-h-10 cursor-pointer rounded-class-btn border-0 bg-class-white px-4 font-[inherit] font-bold text-class-wrong"
            >
              {strip.action.label}
            </button>
          )}
        </div>
      )}

      {keys}

      {bottom && (
        <div className="flex shrink-0 items-center gap-3 px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] desk:h-[76px] desk:border-t desk:border-class-field desk:bg-class-white desk:px-6 desk:py-0">
          {bottom}
        </div>
      )}
    </div>
  )
}

/* ── the outline ───────────────────────────────────────────────────── */

function Outline({
  groups,
  index,
  visited,
  exerciseOf,
  goTo,
  homework,
  t,
}: {
  groups: ReturnType<typeof outlineOf>
  index: number
  visited: Set<string>
  exerciseOf: (s: Step) => LessonDetail['exercises'][number] | null
  goTo: (i: number) => void
  homework: string | null
  t: PlayerStrings
}) {
  return (
    <>
      <nav aria-label={t.outline} className="flex flex-col gap-[22px]">
        {groups.map((g) => {
          const inside = g.steps.some((s) => s.index === index)
          const started = g.steps.some(({ step }) => {
            const e = exerciseOf(step)
            return e ? Boolean(e.attempt) : visited.has(step.id)
          })
          return (
            <div key={g.key}>
              <p lang="pl" className="m-0 mb-1.5 px-2 text-class-label font-semibold text-class-ink-faint">
                {g.label}
              </p>
              {g.steps.map(({ index: i, step }) => {
                const current = i === index
                const e = exerciseOf(step)
                const status: 'todo' | 'half' | 'done' = e
                  ? e.attempt && !e.attempt.stale
                    ? e.attempt.correct
                      ? 'done'
                      : 'half'
                    : 'todo'
                  : visited.has(step.id) && i < index
                    ? 'done'
                    : visited.has(step.id) && !current
                      ? 'done'
                      : 'todo'
                // A part not started yet folds to its name — the outline stays short.
                if (!inside && !started) return null
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-current={current ? 'step' : undefined}
                    className={[
                      'flex w-full cursor-pointer items-center gap-2.5 rounded-class-sm border-0 px-2 py-[7px] text-left font-[inherit]',
                      current ? 'bg-class-white font-semibold text-class-sky-700' : status === 'todo' ? 'bg-transparent text-class-ink-soft' : 'bg-transparent text-class-ink',
                    ].join(' ')}
                  >
                    <Dot status={current ? 'current' : status} />
                    <span lang="pl" className="min-w-0 flex-1 truncate">
                      {step.label}
                    </span>
                    {e && e.attempt && !e.attempt.correct && !e.attempt.stale && e.attempt.max > 0 && (
                      <span className="text-class-label text-class-ink-soft">
                        {e.attempt.score}/{e.attempt.max}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )
        })}
      </nav>
      {homework && (
        <Link
          to={homework}
          className="mt-auto flex items-center gap-2.5 border-t border-class-field px-2 pt-3.5 font-semibold !text-class-sky-700"
        >
          <Icon name="homework" size={20} />
          <span lang="pl">Praca domowa</span>
        </Link>
      )}
    </>
  )
}

function Dot({ status }: { status: 'todo' | 'half' | 'done' | 'current' }) {
  if (status === 'current') return <span aria-hidden className="mx-px h-2.5 w-2.5 shrink-0 rounded-full bg-class-sky-700" />
  if (status === 'done') return <span aria-hidden className="h-3 w-3 shrink-0 rounded-full bg-class-gold" />
  if (status === 'half')
    return (
      <span
        aria-hidden
        className="h-3 w-3 shrink-0 rounded-full shadow-[inset_0_0_0_2px_var(--color-class-gold)]"
        style={{ background: 'linear-gradient(90deg, var(--color-class-gold) 50%, #ffffff 50%)' }}
      />
    )
  return <span aria-hidden className="h-3 w-3 shrink-0 rounded-full shadow-[inset_0_0_0_1px_#b8c9d8]" />
}
